import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(params.get("error") === "google" ? "Google sign-in failed. Please try again." : "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate(user.role === "admin" ? "/admin" : "/book", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="signin-page">
      <h1 className="text-3xl sm:text-4xl tracking-tight text-slate-900">Sign In</h1>
      <p className="mt-3 text-sm text-slate-600">
        Sign in to book appointments and see your visit history.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5 bg-white border border-slate-100 rounded-2xl p-8">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={form.email}
            data-testid="signin-email-input"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-xl h-11 focus:ring-2 focus:ring-sky-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            required
            value={form.password}
            data-testid="signin-password-input"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded-xl h-11 focus:ring-2 focus:ring-sky-500"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" data-testid="signin-error">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="signin-submit-btn"
          className="w-full rounded-full h-11 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
        >
          {busy ? "Signing in…" : "Sign In"}
        </Button>

        <div className="relative py-2 text-center">
          <span className="text-xs uppercase tracking-widest text-slate-400 bg-white px-3 relative z-10">or</span>
          <span className="absolute left-0 right-0 top-1/2 h-px bg-slate-200" />
        </div>

        <GoogleSignInButton />

        <p className="text-sm text-slate-600 text-center pt-2">
          New patient?{" "}
          <Link to="/register" className="text-sky-600 hover:underline" data-testid="signin-create-account-link">
            Create Account
          </Link>
        </p>
      </form>
    </div>
  );
}
